import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Megaphone, UserCheck, Loader2, ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Page, PageTitle, Field } from "@/components/site/Common";
import { useMe } from "@/hooks/useMe";
import { TRADES, CITIES } from "@/lib/hz";
import { api, get, post, errMsg } from "@/lib/api";

const uploadPhotos = async (jobId, files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  await api.post(`/jobs/${jobId}/photos`, fd);
};

export default function NewJob() {
  const [sp] = useSearchParams();
  const { data: me } = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState({ trade: sp.get("oficio") || "fontaneria", title: sp.get("titulo") || "", description: "", city: sp.get("ciudad") || me?.city || "Madrid", address: me?.address || "", urgency: sp.get("urgencia") === "urgente" ? "urgente" : "normal", budget: "", mode: sp.get("pro") ? "directo" : "publicado", pro_id: sp.get("pro") || "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const pros = useQuery({ queryKey: ["pros", f.trade, ""], queryFn: () => get(`/pros?trade=${f.trade}`), enabled: f.mode === "directo" });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (f.mode === "directo" && !f.pro_id) return toast.error("Elige un profesional");
    setBusy(true);
    try {
      const job = await post("/jobs", { ...f, budget: f.budget ? Number(f.budget) : null, pro_id: f.mode === "directo" ? f.pro_id : null });
      if (photos.length) await uploadPhotos(job.id, photos).catch((er) => toast.error(errMsg(er, "El trabajo se creó, pero no se pudieron subir las fotos")));
      qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success(f.mode === "directo" ? "Solicitud enviada al profesional" : "Trabajo publicado. Pronto recibirás presupuestos");
      nav(`/trabajos/${job.id}`);
    } catch (er) { toast.error(errMsg(er, "No se pudo crear la solicitud")); }
    finally { setBusy(false); }
  };

  const modeBtn = (m, Icon, title, text) => (
    <button type="button" data-testid={`job-mode-${m}`} onClick={() => setF({ ...f, mode: m })}
      className={`flex flex-1 items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${f.mode === m ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
      <Icon className={`mt-0.5 size-5 ${f.mode === m ? "text-orange-600" : "text-slate-400"}`} />
      <span><span className="block font-semibold">{title}</span><span className="text-xs text-slate-500">{text}</span></span>
    </button>
  );

  return (
    <Page className="max-w-3xl">
      <PageTitle eyebrow="Nueva solicitud" title="¿Qué necesitas?" sub="Publica tu trabajo para recibir presupuestos o envíalo directamente a un profesional." />
      <form onSubmit={submit} className="mt-8 space-y-5 rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="new-job-form">
        <div className="flex flex-col gap-3 sm:flex-row">
          {modeBtn("publicado", Megaphone, "Recibir presupuestos", "Los profesionales del oficio te envían precio")}
          {modeBtn("directo", UserCheck, "Elegir profesional", "Solicitud directa a quien tú elijas")}
        </div>
        <Field label="Oficio">
          <div className="flex flex-wrap gap-2">
            {TRADES.map((t) => (
              <button key={t.id} type="button" onClick={() => setF({ ...f, trade: t.id, pro_id: "" })} data-testid={`job-trade-${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${f.trade === t.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"}`}><t.icon className="size-4" />{t.name}</button>
            ))}
          </div>
        </Field>
        {f.mode === "directo" && (
          <Field label="Profesional">
            <Select value={f.pro_id} onValueChange={set("pro_id")}>
              <SelectTrigger className="h-11" data-testid="job-pro-select"><SelectValue placeholder={pros.isLoading ? "Cargando…" : "Elige profesional"} /></SelectTrigger>
              <SelectContent>{(pros.data ?? []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city} · {p.rating.toFixed(1)}★</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Título"><Input required value={f.title} onChange={set("title")} placeholder="Ej. Fuga bajo el fregadero" className="h-11" data-testid="job-title-input" /></Field>
        <Field label="Descripción"><Textarea required rows={4} value={f.description} onChange={set("description")} placeholder="Explica qué pasa, medidas, materiales…" data-testid="job-description-input" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Ciudad">
            <Select value={f.city} onValueChange={set("city")}>
              <SelectTrigger className="h-11" data-testid="job-city-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Presupuesto máximo (€, opcional)"><Input type="number" min="0" value={f.budget} onChange={set("budget")} className="h-11" data-testid="job-budget-input" /></Field>
        </div>
        <Field label="Dirección"><Input value={f.address} onChange={set("address")} className="h-11" data-testid="job-address-input" /></Field>
        <Field label="Urgencia">
          <div className="flex gap-2">
            {["normal", "urgente"].map((u) => (
              <button key={u} type="button" data-testid={`job-urgency-${u}`} onClick={() => setF({ ...f, urgency: u })}
                className={`h-11 flex-1 rounded-xl border-2 text-sm font-semibold capitalize transition-colors ${f.urgency === u ? (u === "urgente" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-orange-500 bg-orange-50 text-orange-700") : "border-slate-200 text-slate-600"}`}>{u}</button>
            ))}
          </div>
        </Field>
        <Field label="Fotos (opcional)">
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-sm text-slate-600 hover:border-orange-300">
            <ImagePlus className="size-5 text-orange-600" />Añadir fotos
            <input type="file" accept="image/*" multiple className="hidden" data-testid="job-photos-input" onChange={(e) => setPhotos([...photos, ...Array.from(e.target.files)].slice(0, 5))} />
          </label>
          {!!photos.length && (
            <div className="mt-2 flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs">{p.name}<button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}><X className="size-3" /></button></span>
              ))}
            </div>
          )}
        </Field>
        <Button type="submit" size="lg" className="h-12 w-full rounded-xl" disabled={busy} data-testid="job-submit-btn">
          {busy && <Loader2 className="animate-spin" />}{f.mode === "directo" ? "Enviar solicitud al profesional" : "Publicar y recibir presupuestos"}
        </Button>
      </form>
    </Page>
  );
}
